export interface WorkflowKnowledgeContextItem {
  id?: string
  title: string
  content: string
  source: string
  score?: number | null
  created_at?: string | null
  metadata?: Record<string, any> | null
}

export interface WorkflowKnowledgeImageSelectionItem {
  sceneNumber: number
  title: string
  image_url: string
  source: string
  content?: string
  metadata?: Record<string, any> | null
}

export interface WorkflowKnowledgeConsistencyAssetImageSelectionItem {
  assetType: 'character' | 'scene' | 'prop'
  assetId: string
  material_id?: number
  title: string
  image_url: string
  source: string
  content?: string
  metadata?: Record<string, any> | null
}

export interface WorkflowKnowledgeAudioSelectionItem {
  role: 'narration' | 'bgm'
  material_id?: number
  name: string
  url: string
  source: string
  duration_seconds?: number | null
  metadata?: Record<string, any> | null
}

function getStorageKey(projectId: number) {
  return `workflow-knowledge-context:${projectId}`
}

function getImageStorageKey(projectId: number) {
  return `workflow-knowledge-image:${projectId}`
}

function getConsistencyAssetImageStorageKey(projectId: number) {
  return `workflow-knowledge-consistency-asset-image:${projectId}`
}

function getAudioStorageKey(projectId: number) {
  return `workflow-knowledge-audio:${projectId}`
}

function dedupeContexts(items: WorkflowKnowledgeContextItem[]) {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = `${item.title}::${item.content}::${item.source}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

export function queueWorkflowKnowledgeContexts(projectId: number, items: WorkflowKnowledgeContextItem[]) {
  if (typeof window === 'undefined' || !items.length) {
    return
  }

  const storageKey = getStorageKey(projectId)
  const existing = readWorkflowKnowledgeContexts(projectId)
  const merged = dedupeContexts([...existing, ...items]).map((item) => ({
    ...item,
    id: item.id || `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
  }))

  sessionStorage.setItem(storageKey, JSON.stringify(merged))
}

export function readWorkflowKnowledgeContexts(projectId: number): WorkflowKnowledgeContextItem[] {
  if (typeof window === 'undefined') {
    return []
  }

  const raw = sessionStorage.getItem(getStorageKey(projectId))
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return dedupeContexts(parsed.filter((item) => item && typeof item === 'object'))
  } catch {
    return []
  }
}

export function consumeWorkflowKnowledgeContexts(projectId: number): WorkflowKnowledgeContextItem[] {
  if (typeof window === 'undefined') {
    return []
  }

  const storageKey = getStorageKey(projectId)
  const items = readWorkflowKnowledgeContexts(projectId)
  sessionStorage.removeItem(storageKey)
  return items
}

export function queueWorkflowKnowledgeImageSelections(projectId: number, items: WorkflowKnowledgeImageSelectionItem[]) {
  if (typeof window === 'undefined' || !items.length) {
    return
  }

  const storageKey = getImageStorageKey(projectId)
  const existing = readWorkflowKnowledgeImageSelections(projectId)
  const merged = [...existing]
  const seen = new Set(existing.map((item) => `${item.sceneNumber}::${item.image_url}`))

  for (const item of items) {
    const key = `${item.sceneNumber}::${item.image_url}`
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    merged.push(item)
  }

  sessionStorage.setItem(storageKey, JSON.stringify(merged))
}

export function readWorkflowKnowledgeImageSelections(projectId: number): WorkflowKnowledgeImageSelectionItem[] {
  if (typeof window === 'undefined') {
    return []
  }

  const raw = sessionStorage.getItem(getImageStorageKey(projectId))
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((item) => item && typeof item === 'object' && item.sceneNumber && item.image_url)
  } catch {
    return []
  }
}

export function consumeWorkflowKnowledgeImageSelections(projectId: number): WorkflowKnowledgeImageSelectionItem[] {
  if (typeof window === 'undefined') {
    return []
  }

  const storageKey = getImageStorageKey(projectId)
  const items = readWorkflowKnowledgeImageSelections(projectId)
  sessionStorage.removeItem(storageKey)
  return items
}

export function queueWorkflowKnowledgeConsistencyAssetImageSelections(
  projectId: number,
  items: WorkflowKnowledgeConsistencyAssetImageSelectionItem[],
) {
  if (typeof window === 'undefined' || !items.length) {
    return
  }

  const storageKey = getConsistencyAssetImageStorageKey(projectId)
  const existing = readWorkflowKnowledgeConsistencyAssetImageSelections(projectId)
  const merged = [...existing]
  const seen = new Set(existing.map((item) => `${item.assetType}::${item.assetId}::${item.image_url}`))

  for (const item of items) {
    const key = `${item.assetType}::${item.assetId}::${item.image_url}`
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    merged.push(item)
  }

  sessionStorage.setItem(storageKey, JSON.stringify(merged))
}

export function readWorkflowKnowledgeConsistencyAssetImageSelections(projectId: number): WorkflowKnowledgeConsistencyAssetImageSelectionItem[] {
  if (typeof window === 'undefined') {
    return []
  }

  const raw = sessionStorage.getItem(getConsistencyAssetImageStorageKey(projectId))
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((item) => (
      item
      && typeof item === 'object'
      && ['character', 'scene', 'prop'].includes(String(item.assetType || ''))
      && String(item.assetId || '').trim()
      && String(item.image_url || '').trim()
    ))
  } catch {
    return []
  }
}

export function consumeWorkflowKnowledgeConsistencyAssetImageSelections(projectId: number): WorkflowKnowledgeConsistencyAssetImageSelectionItem[] {
  if (typeof window === 'undefined') {
    return []
  }

  const storageKey = getConsistencyAssetImageStorageKey(projectId)
  const items = readWorkflowKnowledgeConsistencyAssetImageSelections(projectId)
  sessionStorage.removeItem(storageKey)
  return items
}

export function queueWorkflowKnowledgeAudioSelections(projectId: number, items: WorkflowKnowledgeAudioSelectionItem[]) {
  if (typeof window === 'undefined' || !items.length) {
    return
  }

  const storageKey = getAudioStorageKey(projectId)
  const existing = readWorkflowKnowledgeAudioSelections(projectId)
  const mergedByRole = new Map(existing.map((item) => [item.role, item] as const))

  for (const item of items) {
    if (!item || !String(item.url || '').trim()) {
      continue
    }

    mergedByRole.set(item.role, {
      ...item,
      name: String(item.name || '').trim() || (item.role === 'narration' ? '旁白素材' : 'BGM 素材'),
      url: String(item.url || '').trim(),
      source: String(item.source || 'material').trim() || 'material',
    })
  }

  sessionStorage.setItem(storageKey, JSON.stringify(Array.from(mergedByRole.values())))
}

export function readWorkflowKnowledgeAudioSelections(projectId: number): WorkflowKnowledgeAudioSelectionItem[] {
  if (typeof window === 'undefined') {
    return []
  }

  const raw = sessionStorage.getItem(getAudioStorageKey(projectId))
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((item) => (
      item
      && typeof item === 'object'
      && ['narration', 'bgm'].includes(String(item.role || ''))
      && String(item.url || '').trim()
    ))
  } catch {
    return []
  }
}

export function consumeWorkflowKnowledgeAudioSelections(projectId: number): WorkflowKnowledgeAudioSelectionItem[] {
  if (typeof window === 'undefined') {
    return []
  }

  const storageKey = getAudioStorageKey(projectId)
  const items = readWorkflowKnowledgeAudioSelections(projectId)
  sessionStorage.removeItem(storageKey)
  return items
}